/**
 * Report Parameter Validation Utilities
 *
 * Validation functions that can be used on both frontend and backend.
 */
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

import { ReportDefinition } from './definitions';
import {
  BooleanParameter,
  DateParameter,
  DateRangeParameter,
  DateTimeParameter,
  DateTimeRangeParameter,
  MultiSelectParameter,
  NumericParameter,
  ParameterValueMap,
  RelativeDateOffset,
  ReportParameter,
  SelectParameter,
  TextParameter,
} from './parameters';

// Enable quarter support for dayjs
dayjs.extend(quarterOfYear);

// =============================================================================
// Validation Result Types
// =============================================================================

/**
 * Result of validating a single parameter
 */
export interface ParameterValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Result of validating all parameters for a report
 */
export interface ReportValidationResult {
  valid: boolean;
  parameterErrors: Record<string, string[]>;
  generalErrors: string[];
}

// =============================================================================
// Date Utilities
// =============================================================================

/**
 * Calculate a date from a relative offset
 */
export function calculateRelativeDate(baseDate: Date, offset: RelativeDateOffset): Date {
  return dayjs(baseDate).add(offset.value, offset.unit).toDate();
}

/**
 * Format a date for display in error messages
 */
function formatDate(date: Date): string {
  return date.toLocaleDateString();
}

// =============================================================================
// Parameter Validators
// =============================================================================

/**
 * Validate a date parameter
 */
export function validateDateParameter(
  param: DateParameter | DateTimeParameter,
  value: ParameterValueMap['date'],
): ParameterValidationResult {
  const errors: string[] = [];

  if (param.required && !value) {
    errors.push(`${param.label} is required`);
    return { valid: false, errors };
  }

  if (value && param.config) {
    const date = new Date(value);
    const today = new Date();

    if (param.config.minDateOffset) {
      const minDate = calculateRelativeDate(today, param.config.minDateOffset);
      if (date < minDate) {
        errors.push(`${param.label} cannot be before ${formatDate(minDate)}`);
      }
    }

    if (param.config.maxDateOffset) {
      const maxDate = calculateRelativeDate(today, param.config.maxDateOffset);
      if (date > maxDate) {
        errors.push(`${param.label} cannot be after ${formatDate(maxDate)}`);
      }
    }

    if (!param.config.allowFutureDates && date > today) {
      errors.push(`${param.label} cannot be in the future`);
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate a date range parameter
 */
export function validateDateRangeParameter(
  param: DateRangeParameter | DateTimeRangeParameter,
  value: ParameterValueMap['date_range'],
): ParameterValidationResult {
  const errors: string[] = [];

  if (param.required && (!value || !value.start || !value.end)) {
    errors.push(`${param.label} is required`);
    return { valid: false, errors };
  }

  if (value && value.start && value.end) {
    const start = new Date(value.start);
    const end = new Date(value.end);
    const today = new Date();

    if (start > end) {
      errors.push('Start date must be before end date');
    }

    if (param.config) {
      if (param.config.minDateOffset) {
        const minDate = calculateRelativeDate(today, param.config.minDateOffset);
        if (start < minDate) {
          errors.push(`Start date cannot be before ${formatDate(minDate)}`);
        }
      }

      if (param.config.maxDateOffset) {
        const maxDate = calculateRelativeDate(today, param.config.maxDateOffset);
        if (end > maxDate) {
          errors.push(`End date cannot be after ${formatDate(maxDate)}`);
        }
      }

      if (!param.config.allowFutureDates) {
        if (start > today) {
          errors.push('Start date cannot be in the future');
        }
        if (end > today) {
          errors.push('End date cannot be in the future');
        }
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate a select parameter
 */
export function validateSelectParameter(
  param: SelectParameter,
  value: ParameterValueMap['select'],
): ParameterValidationResult {
  const errors: string[] = [];

  if (param.required && !value) {
    errors.push(`${param.label} is required`);
    return { valid: false, errors };
  }

  if (value && param.config.options.length > 0) {
    const validValues = param.config.options.map((o) => o.value);
    if (!validValues.includes(value)) {
      errors.push(`Invalid selection for ${param.label}`);
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate a multi-select parameter
 */
export function validateMultiSelectParameter(
  param: MultiSelectParameter,
  value: ParameterValueMap['multi_select'],
): ParameterValidationResult {
  const errors: string[] = [];
  const values = value || [];

  if (param.required && values.length === 0) {
    errors.push(`${param.label} is required`);
    return { valid: false, errors };
  }

  if (param.config.minSelection && values.length < param.config.minSelection) {
    errors.push(
      `Select at least ${param.config.minSelection} option${param.config.minSelection > 1 ? 's' : ''} for ${param.label}`,
    );
  }

  if (param.config.maxSelection && values.length > param.config.maxSelection) {
    errors.push(
      `Select at most ${param.config.maxSelection} option${param.config.maxSelection > 1 ? 's' : ''} for ${param.label}`,
    );
  }

  if (values.length > 0 && param.config.options.length > 0) {
    const validValues = param.config.options.map((o) => o.value);
    const invalidValues = values.filter((v) => !validValues.includes(v));
    if (invalidValues.length > 0) {
      errors.push(`Invalid selection(s) for ${param.label}`);
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate a numeric parameter
 */
export function validateNumericParameter(
  param: NumericParameter,
  value: ParameterValueMap['number'],
): ParameterValidationResult {
  const errors: string[] = [];

  if (param.required && (value === null || value === undefined)) {
    errors.push(`${param.label} is required`);
    return { valid: false, errors };
  }

  if (value !== null && value !== undefined && param.config) {
    if (param.config.min !== undefined && value < param.config.min) {
      errors.push(`${param.label} must be at least ${param.config.min}`);
    }

    if (param.config.max !== undefined && value > param.config.max) {
      errors.push(`${param.label} must be at most ${param.config.max}`);
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate a boolean parameter
 */
export function validateBooleanParameter(
  param: BooleanParameter,
  value: ParameterValueMap['boolean'],
): ParameterValidationResult {
  // Boolean parameters are always valid (they default to false)
  return { valid: true, errors: [] };
}

/**
 * Validate a text parameter
 */
export function validateTextParameter(
  param: TextParameter,
  value: ParameterValueMap['text'],
): ParameterValidationResult {
  const errors: string[] = [];
  const textValue = value || '';

  if (param.required && !textValue.trim()) {
    errors.push(`${param.label} is required`);
    return { valid: false, errors };
  }

  if (textValue && param.config) {
    if (param.config.minLength && textValue.length < param.config.minLength) {
      errors.push(`${param.label} must be at least ${param.config.minLength} characters`);
    }

    if (param.config.maxLength && textValue.length > param.config.maxLength) {
      errors.push(`${param.label} must be at most ${param.config.maxLength} characters`);
    }

    if (param.config.pattern) {
      const regex = new RegExp(param.config.pattern);
      if (!regex.test(textValue)) {
        errors.push(param.config.patternMessage || `${param.label} has an invalid format`);
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

// =============================================================================
// Master Validator
// =============================================================================

/**
 * Validate a single parameter value
 */
export function validateParameter(param: ReportParameter, value: unknown): ParameterValidationResult {
  switch (param.type) {
    case 'date':
      return validateDateParameter(param, value as ParameterValueMap['date']);
    case 'datetime':
      return validateDateParameter(param, value as ParameterValueMap['datetime']);
    case 'date_range':
      return validateDateRangeParameter(param, value as ParameterValueMap['date_range']);
    case 'datetime_range':
      return validateDateRangeParameter(param, value as ParameterValueMap['datetime_range']);
    case 'select':
      return validateSelectParameter(param, value as ParameterValueMap['select']);
    case 'multi_select':
      return validateMultiSelectParameter(param, value as ParameterValueMap['multi_select']);
    case 'number':
      return validateNumericParameter(param, value as ParameterValueMap['number']);
    case 'boolean':
      return validateBooleanParameter(param, value as ParameterValueMap['boolean']);
    case 'text':
      return validateTextParameter(param, value as ParameterValueMap['text']);
    default:
      return { valid: true, errors: [] };
  }
}

/**
 * Validate all parameters for a report
 */
export function validateReportParameters(
  report: ReportDefinition,
  values: Record<string, unknown>,
): ReportValidationResult {
  const parameterErrors: Record<string, string[]> = {};
  const generalErrors: string[] = [];

  for (const param of report.parameters) {
    const result = validateParameter(param, values[param.name]);
    if (!result.valid) {
      parameterErrors[param.name] = result.errors;
    }
  }

  return {
    valid: Object.keys(parameterErrors).length === 0 && generalErrors.length === 0,
    parameterErrors,
    generalErrors,
  };
}
